/**
 * Care-Time Quick-Add Modal.
 *
 * Self-contained Mantine modal that captures minutes + notes for a
 * single care-time entry. Used by:
 *  - Patient profile quick-add button (method: patient-profile-quick-add)
 *  - Action-list row affordance (method: action-list-quick-add)
 *  - Activity-log drawer inline edit (method: provided by caller)
 *
 * The caller controls `opened`/`onClose` and supplies context
 * (`patientId`, `performerRoleId`, optional `taskId`/`carePlanId`).
 *
 * @agentic connections
 */

import { startTransition, useEffect, useState } from "react";
import { Modal } from "@benchmark/ui/components/atomic";
import { Alert, Button, Group, Stack, TextInput } from "@mantine/core";
import { IconAlertCircle, IconClockPlay } from "@tabler/icons-react";
import { useMedplum } from "@medplum/react";
import { notifications } from "@mantine/notifications";
import type { Observation } from "@medplum/fhirtypes";
import type { CareTimeMethod } from "@benchmark/core/constants";
import { isCareTimeNonBillable } from "@benchmark/core/constants";
import { CareTimeInlineFields } from "./CareTimeInlineFields";
import { DialpadCallPicker } from "./DialpadCallPicker";
import { createCareTimeFacade } from "@/facades/care-time.facade";
import type { RecentCall } from "@/hooks/domain/useRecentPatientCalls";

/**
 * Props for `CareTimeQuickAddModal`. @agentic connections
 */
export type CareTimeQuickAddModalProps = {
  opened: boolean;
  onClose: () => void;
  /** Source surface code per D3. */
  method: CareTimeMethod;
  patientId: string;
  patientDisplay: string;
  performerRoleId: string;
  performerDisplay: string;
  /** Optional task to anchor on `focus`. */
  taskId?: string;
  taskDisplay?: string;
  /** Optional care plan to anchor on `basedOn`. */
  carePlanId?: string;
  carePlanDisplay?: string;
  /** Optional encounter to anchor on `encounter`. */
  encounterId?: string;
  /** Existing Observation when editing an entry (drawer use-case). */
  existingObservation?: Observation;
  /** Modal title. Defaults to "Log care time". */
  title?: string;
  /** Notify the caller when the write succeeds. */
  onSuccess?: (observation: Observation) => void;
};

const todayDate = (): string => new Date().toISOString().slice(0, 10);

/**
 * Quick-add modal for care-time entries.
 * @agentic connections
 */
export const CareTimeQuickAddModal = ({
  opened,
  onClose,
  method,
  patientId,
  patientDisplay,
  performerRoleId,
  performerDisplay,
  taskId,
  taskDisplay,
  carePlanId,
  carePlanDisplay,
  encounterId,
  existingObservation,
  title = "Log care time",
  onSuccess,
}: CareTimeQuickAddModalProps) => {
  const medplum = useMedplum();
  const [minutes, setMinutes] = useState<number | undefined>(
    existingObservation?.valueQuantity?.value,
  );
  const [notes, setNotes] = useState<string>(
    existingObservation?.note?.[0]?.text ?? "",
  );
  const [date, setDate] = useState<string>(
    existingObservation?.effectiveDateTime?.slice(0, 10) ?? todayDate(),
  );
  const [nonBillable, setNonBillable] = useState<boolean>(
    existingObservation ? isCareTimeNonBillable(existingObservation) : false,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // The clinician explicitly links the call they're documenting (DD8) — no
  // silent inference. Selecting a call sets the minutes and records the link.
  const [selectedCall, setSelectedCall] = useState<RecentCall | undefined>(
    undefined,
  );

  // Reset local draft state each time the modal opens.
  useEffect(() => {
    if (!opened) return;
    startTransition(() => {
      setMinutes(existingObservation?.valueQuantity?.value);
      setNotes(existingObservation?.note?.[0]?.text ?? "");
      setDate(
        existingObservation?.effectiveDateTime?.slice(0, 10) ?? todayDate(),
      );
      setNonBillable(
        existingObservation
          ? isCareTimeNonBillable(existingObservation)
          : false,
      );
      setError(undefined);
      setSelectedCall(undefined);
    });
  }, [opened, existingObservation]);

  const handleSelectCall = (call: RecentCall | undefined) => {
    setSelectedCall(call);
    if (call) setMinutes(call.minutes);
  };

  const isUpdate = Boolean(existingObservation?.id);

  const handleSubmit = async () => {
    if (!minutes || minutes < 1) {
      setError("Enter at least 1 minute of care time.");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      // Resolve effectiveDate:
      //  - Editing an existing entry without changing the date:
      //    preserve the original `effectiveDateTime` so the activity-
      //    log row keeps its original "today at HH:MM" anchor instead
      //    of getting silently overwritten with the modal-open time.
      //  - Otherwise (create today, or edit that re-anchors the day):
      //    today gets the actual capture timestamp so the row shows
      //    "today at HH:MM"; backdated days keep the noon-UTC anchor
      //    to avoid timezone drift.
      const originalIso = existingObservation?.effectiveDateTime;
      const originalDateString = originalIso?.slice(0, 10);
      const dateUnchanged = isUpdate && date === originalDateString;
      const isToday = date === todayDate();
      const effectiveDate =
        dateUnchanged && originalIso
          ? originalIso
          : isToday
            ? new Date().toISOString()
            : new Date(`${date}T12:00:00.000Z`).toISOString();
      const facade = createCareTimeFacade(medplum);
      const { observation } = await facade.upsert({
        formData: {
          minutes,
          method,
          notes: notes.trim() || undefined,
          effectiveDate,
          patientId,
          patientDisplay,
          performerRoleId,
          performerDisplay,
          taskId,
          taskDisplay,
          carePlanId,
          carePlanDisplay,
          encounterId,
          nonBillable,
          ...(selectedCall && {
            communicationId: selectedCall.communicationId,
          }),
          existingObservationId: existingObservation?.id,
        },
        existingObservation,
      });

      notifications.show({
        title: isUpdate ? "Care time updated" : "Care time logged",
        message: `${minutes} min recorded for ${patientDisplay}.`,
        color: "teal",
        icon: <IconClockPlay size={16} />,
      });
      onSuccess?.(observation);
      onClose();
    } catch (err) {
      console.error("Care-time write failed:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Could not save care time. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md">
      <Stack gap="md">
        <TextInput
          label="Date"
          type="date"
          value={date}
          onChange={(event) => setDate(event.currentTarget.value)}
          disabled={submitting}
          required
        />
        <CareTimeInlineFields
          minutes={minutes}
          onMinutesChange={setMinutes}
          notes={notes}
          onNotesChange={setNotes}
          label="Minutes"
          helperText="Whole minutes of care time spent on this patient."
          required
          disabled={submitting}
          showNonBillableToggle
          nonBillable={nonBillable}
          onNonBillableChange={setNonBillable}
        />
        {!existingObservation ? (
          <DialpadCallPicker
            patientId={patientId}
            practitionerRoleId={performerRoleId}
            selectedCommunicationId={selectedCall?.communicationId}
            onSelect={handleSelectCall}
          />
        ) : null}
        {error ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            leftSection={<IconClockPlay size={16} />}
          >
            {isUpdate ? "Save changes" : "Log care time"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
